import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useListZoneUserAccess } from 'src/features/parkingManagement/api/ZoneUserAccess/useListZoneUserAccess';
import ZoneUserAccessFormModal from 'src/features/parkingManagement/components/ZoneUserAccess/forms/ZoneUserAccessFormModal';
import CellZoneUserAccessActions from 'src/features/parkingManagement/components/ZoneUserAccess/ZoneUserAccessActions';
import { type ZoneUserAccess } from 'src/features/parkingManagement/types/zoneUserAccess.ts';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { membershipRoleOptions } from 'src/features/userManagement/utils';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import {
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useTitleStore } from 'src/stores/useTitleStore';

function ZoneUserAccessTable() {
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();

    const {
        data: zoneUserAccessData,
        isLoading,
        isError,
        refetch,
    } = useListZoneUserAccess({ parkingId: parkingId || '', config: { enabled: !!parkingId } });

    const [zoneUserAccessesModalData, setZoneUserAccessesModalData] = React.useState<{
        selectedZoneUserAccess: null | ZoneUserAccess;
        isDelete: boolean;
    }>({
        selectedZoneUserAccess: null,
        isDelete: false,
    });

    const { data } = useGetParking({ parkingId: parkingId || '', config: { enabled: !!parkingId } });

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const { ...pagination } = usePagination();
    const { ...search } = useSearch();
    const columnHelper = createColumnHelper<ZoneUserAccess>();

    const columns = [
        columnHelper.accessor('zone_id', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(parkingManagementMessages.zoneId),
            },
        }),
        columnHelper.accessor('user_roles', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(parkingManagementMessages.zoneUserAccessRoles),
            },
            cell: ({ row }) => {
                const labels = row.original.user_roles
                    .map((recipient: MEMBERSHIP_ROLES) => {
                        const label = membershipRoleOptions.find((role) => role.value === recipient)?.label;
                        return label ? formatMessage(label) : null;
                    })
                    .filter(Boolean);

                return <div>{labels.join(', ')}</div>;
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellZoneUserAccessActions
                    deleteCallback={() =>
                        setZoneUserAccessesModalData({ selectedZoneUserAccess: props.row.original, isDelete: true })
                    }
                    editCallback={() =>
                        setZoneUserAccessesModalData({ selectedZoneUserAccess: props.row.original, isDelete: false })
                    }
                    tooltipMessage={parkingManagementMessages.access}
                    {...props}
                />
            ),
        }),
    ];

    const resetZoneUserAccessData = () =>
        setZoneUserAccessesModalData({ selectedZoneUserAccess: null, isDelete: false });

    return (
        <>
            {zoneUserAccessesModalData.selectedZoneUserAccess && (
                <ZoneUserAccessFormModal
                    selectedZoneUserAccess={zoneUserAccessesModalData.selectedZoneUserAccess}
                    isDialogOpen={!!zoneUserAccessesModalData.selectedZoneUserAccess}
                    isDelete={zoneUserAccessesModalData.isDelete}
                    removeTrigger
                    onCloseDialog={resetZoneUserAccessData}
                    onSubmit={() => {
                        resetZoneUserAccessData();
                        refetch();
                    }}
                />
            )}
            <DataTable
                columns={columns}
                data={zoneUserAccessData}
                pagination={pagination}
                search={search}
                semantics={{
                    title: formatMessage(parkingManagementMessages.zoneUserAccess),
                    entryName: formatMessage(parkingManagementMessages.access),
                    entryNames: formatMessage(parkingManagementMessages.accesses),
                }}
                isError={isError}
                isLoading={isLoading}
                refetch={refetch}
                components={{
                    newEntry: ZoneUserAccessFormModal,
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
        </>
    );
}

export default ZoneUserAccessTable;
